<script>
import prettyBytes from 'pretty-bytes';

/* COMPOSANTS */
import FileUpload from 'primevue/fileupload';
import AkLabel from './AkLabel.vue';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';
import loaderMixin from '@mixins/loaderMixin';
import fileUploadMixin from '@mixins/fileUploadMixin';

/* SERVICES */
import ficService from '@services/ficService';

export default {
  components: {AkLabel, FileUpload},
  inject: {pSubmitted: {default: null}, pDisabled: {default: null}, pIgnoredValidator: {default: null}},
  mixins: [messageMixin, fileUploadMixin, loaderMixin],
  props: {
    modelValue: {
      required: true,
    },
    tableName: {
      type: String,
      required: true,
    },
    tableId: {
      type: Number,
      required: true,
    },
    ficType: {
      type: String,
      required: false,
      default: null,
    },
    ficPreview: {
      type: Boolean,
      required: false,
      default: false,
    },
    chooseLabel: {
      type: String,
      required: false,
      default: 'Ajouter fichier',
    },
    title: {
      type: String,
      required: false,
    },
    accept: {
      type: String,
      required: false,
      default: '',
    },
    customDelete: {
      type: Boolean,
      required: false,
      default: false,
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    secondOption: {
      type: Object,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    information: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: ['update:modelValue', 'delete-item'],
  data() {
    return {
      base64: undefined,
      uploadInProgress: false,
    };
  },
  mounted() {
    if (this.ficPreview && this.isDisabled && this.currentValue) {
      this.getPreview();
    }
  },
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    hasCurrentValue() {
      return this.currentValue !== undefined && this.currentValue !== null;
    },
    fileSize() {
      if (!this.hasCurrentValue) {
        return '';
      }
      return prettyBytes(this.currentValue.size, {locale: 'en', maximumFractionDigits: 1});
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        if (this.validator.$errors) {
          for (let error of this.validator.$errors) {
            if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
              return true;
            }
          }
        } else {
          if (this.hasValidator) {
            for (let p of Object.keys(this.validator)) {
              if (
                this.getIgnoredValidator.indexOf(p) === -1 &&
                this.validator[p] === false &&
                p.startsWith('$') === false &&
                this.validator.$invalid
              ) {
                return true;
              }
            }
          } else return false;
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params === undefined ||
          this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },
    isRequiredFailed() {
      return (
        this.isRequired &&
        this.getIgnoredValidator.indexOf('required') === -1 &&
        (this.validator.required.$invalid || this.validator.$invalid) &&
        this.alreadySubmitted
      );
    },
  },
  methods: {
    delete() {
      this.$emit('delete-item');
    },
    getFileIcon() {
      return 'ga-document';
    },
    getFileName() {
      if (!this.hasCurrentValue) return '';
      return this.currentValue.fileName;
    },
    getColorIcon() {
      return 'text-grey-900';
    },
    downloadFile(isPreview) {
      if (!this.hasCurrentValue) return;
      ficService.download(this.currentValue, isPreview).catch((e) => {
        if (e.response.status === 404) {
          this.addError("Le fichier n'existe pas !");
        }
      });
    },
    uploadFile() {
      this.$refs.uploader.$refs.fileInput.click();
    },
    clearFile() {
      this.$refs.uploader.clear();
      if (this.customDelete) {
        this.$emit('delete-item');
      } else {
        ficService
          .delete(this.currentValue)
          .then(() => {
            this.currentValue = undefined;
            this.success('Le fichier a bien été supprimé');
          })
          .catch((e) => {
            this.addError('Une erreur est survenue');
          });
      }
    },
    selectFiles(event) {
      if (event.files) {
        let fic = {};
        fic.ficType = this.ficType;
        fic.tableName = this.tableName;
        fic.tableId = this.tableId;
        this.uploadInProgress = true;
        ficService
          .createItem(fic, event.files[0])
          .then((data) => {
            this.currentValue = data;
            this.success('Fichier chargé');
          })
          .catch((e) => {
            this.addError('Une erreur est survenue');
          })
          .finally(() => {
            this.uploadInProgress = false;
          });
      }
    },
    getPreview() {
      ficService
        .preview(this.currentValue)
        .then((data) => {
          this.base64 = data;
        })
        .catch((e) => {
          // Catch l'erreur pour ne pas l'afficher côté IHM
        });
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control">
    <AkLabel v-if="title && displayLabel" :required="this.isRequired" :information="information">
      {{ title }}
    </AkLabel>
    <div class="flex flex-col gap-2" :class="{'cursor-pointer': !this.isDisabled}">
      <img
        v-if="ficPreview && this.isDisabled && base64"
        :src="base64"
        class="cursor-pointer"
        @click="downloadFile(true)" />
      <div
        class="form-fic-input flex gap-2 items-center px-4 border-2 rounded-md border-grey-50 hover:border-secondary bg-grey-50 bg-clip-padding box-border h-[52px]"
        @click="!hasCurrentValue && !isDisabled ? uploadFile() : null"
        :class="[
          hasCurrentValue ? 'py-[5.25px]' : 'py-3',
          this.isDisabled ? 'pointer-events-none opacity-80' : '',
          this.isInvalid && alreadySubmitted ? 'p-invalid' : '',
        ]">
        <div v-if="hasCurrentValue">
          <i :class="[getFileIcon(), getColorIcon()]" class="ga-icon text-3xl" />
        </div>
        <div
          v-if="hasCurrentValue"
          :class="{isDisabled: disabled}"
          class="truncate flex flex-col flex-1 hover:cursor-pointer gap-1 pointer-events-auto"
          @click="downloadFile(true)">
          <span class="pointer text-grey-900 font-medium truncate text-base">{{ getFileName() }}</span>
          <span class="text-grey-400 font-normal text-sm">{{ fileSize }}</span>
        </div>
        <div v-if="hasCurrentValue" class="flex gap-1">
          <span class="hover:cursor-pointer pointer-events-auto" @click="downloadFile(false)">
            <i class="ga-icon ga-download text-blue text-2xl" />
          </span>
          <span v-if="!isDisabled" class="hover:cursor-pointer" @click="clearFile()"
            ><i class="ga-icon ga-trash text-red text-2xl"
          /></span>
        </div>
        <div v-if="!hasCurrentValue && !isDisabled" class="flex items-center gap-2 hover:cursor-pointer leading-[23px]">
          <i :class="[getColorIcon()]" v-if="!uploadInProgress" class="ga-icon ga-plus text-3xl opacity-50" />
          <i :class="[getColorIcon()]" v-else class="ga-icon ga-synchronize animate-spin text-3xl opacity-50" />

          <div v-if="!uploadInProgress">
            <span class="text-grey-900 opacity-50 font-medium text-base">{{ chooseLabel }}</span>
          </div>
          <div v-else>
            <span class="text-grey-900 opacity-50 font-medium text-base">Ajout en cours...</span>
          </div>
        </div>
        <div v-if="!hasCurrentValue && isDisabled" class="flex gap-2 items-center disabled">
          <i :class="[getColorIcon()]" class="ga-icon ga-plus text-2xl leading-[23px]" />
          <div>
            <span class="text-grey-900 opacity-50 font-medium text-base">{{ chooseLabel }}</span>
          </div>
        </div>
      </div>
    </div>
    <small v-if="isRequiredFailed" class="p-error">{{ $t('field_required', [title]) }}</small>

    <FileUpload
      v-if="!isDisabled"
      ref="uploader"
      :accept="accept"
      :auto="true"
      :choose-label="chooseLabel"
      :custom-upload="true"
      :invalidFileSizeMessage="this.getInvalidFileSizeMessage()"
      :invalidFileTypeMessage="this.getInvalidFileTypeMessage(accept)"
      :maxFileSize="getMaxFileUpload()"
      :multiple="false"
      :show-upload-button="false"
      class="form-control hidden"
      mode="basic"
      name="uploader"
      @select="(event) => selectFiles(event)" />
  </div>
</template>
